import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowBack, Save, Delete } from '@mui/icons-material';
import { API_BASE_URL, ENDPOINTS } from '../../config/api';
import Card from '../../components/ui/Card';
import Notification from '../../components/ui/Notification';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import StatusBadge from '../../components/ui/StatusBadge';
import useApi from '../../hooks/useApi';

const BookingFormPage = ({ id: propId }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null,
  });

  // Form state
  const [formData, setFormData] = useState({
    id: '',
    booking_reference: '',
    booking_type: 'destination',
    destination: '',
    excursion: '',
    package: '',
    start_date: '',
    end_date: '',
    travelers: 1,
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    special_requests: '',
    total_price: '',
    custom_price_enabled: false,
    custom_unit_price: '',
    status: 'pending',
  });

  // Check if we're in edit mode
  const isEditMode = Boolean(propId);

  // Fetch destinations, excursions, and packages for dropdowns
  const { data: destinationsData } = useApi(ENDPOINTS.DESTINATIONS, {
    fetchOnMount: true,
  });

  const { data: excursionsData } = useApi(ENDPOINTS.EXCURSIONS, {
    fetchOnMount: true,
  });

  const { data: packagesData } = useApi(ENDPOINTS.PACKAGES, {
    fetchOnMount: true,
  });

  // Fetch booking details if in edit mode
  useEffect(() => {
    const fetchBookingDetails = async () => {
      if (isEditMode) {
        try {
          setLoading(true);
          // Construct URL correctly to avoid double slashes
          const detailUrl = `${API_BASE_URL}/bookings/${propId}/`;
          console.log('Fetching booking details from:', detailUrl);

          const response = await fetch(detailUrl, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
            },
          });

          if (!response.ok) {
            throw new Error('Failed to fetch booking details');
          }

          const data = await response.json();

          // Format dates for form inputs
          const formattedData = {
            ...data.booking,
            start_date: data.booking.start_date ? new Date(data.booking.start_date).toISOString().split('T')[0] : '',
            end_date: data.booking.end_date ? new Date(data.booking.end_date).toISOString().split('T')[0] : '',
          };

          setFormData(formattedData);
        } catch (error) {
          console.error('Error fetching booking details:', error);
          setNotification({
            type: 'error',
            message: 'Erreur lors du chargement des détails de la réservation',
          });
        } finally {
          setLoading(false);
        }
      }
    };

    fetchBookingDetails();
  }, [isEditMode, propId]);

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;

    // Special handling for booking type
    if (name === 'booking_type') {
      // Reset related fields when booking type changes
      setFormData({
        ...formData,
        [name]: value,
        destination: '',
        excursion: '',
        package: '',
        end_date: value === 'destination' ? formData.end_date : '',
      });
    } else {
      const newFormData = {
        ...formData,
        [name]: value,
      };

      // Auto-calculate total price when custom pricing is enabled
      if (formData.custom_price_enabled && (name === 'custom_unit_price' || name === 'travelers')) {
        const unitPrice = name === 'custom_unit_price' ? parseFloat(value) : parseFloat(formData.custom_unit_price);
        const travelers = name === 'travelers' ? parseInt(value) : parseInt(formData.travelers);

        if (unitPrice && travelers && unitPrice > 0 && travelers > 0) {
          newFormData.total_price = (unitPrice * travelers).toFixed(2);
        }
      }

      setFormData(newFormData);
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);

      // Prepare form data
      const bookingData = { ...formData };

      // Remove empty fields
      Object.keys(bookingData).forEach(key => {
        if (bookingData[key] === '') {
          delete bookingData[key];
        }
      });

      // Remove fields not relevant to the booking type
      if (bookingData.booking_type !== 'destination') {
        delete bookingData.destination;
        delete bookingData.end_date;
      }

      if (bookingData.booking_type !== 'excursion') {
        delete bookingData.excursion;
      }

      if (bookingData.booking_type !== 'package') {
        delete bookingData.package;
      }

      let response;

      if (isEditMode) {
        // Update existing booking
        // Construct URL correctly to avoid double slashes
        const updateUrl = `${API_BASE_URL}/bookings/${propId}/update/`;
        console.log('Updating booking at:', updateUrl);

        response = await fetch(updateUrl, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
          },
          body: JSON.stringify(bookingData),
        });
      } else {
        // Create new booking
        // Construct URL correctly to avoid double slashes
        const createUrl = `${API_BASE_URL}/bookings/create/`;
        console.log('Creating booking at:', createUrl);

        response = await fetch(createUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
          },
          body: JSON.stringify(bookingData),
        });
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Error processing booking');
      }

      const responseData = await response.json();

      setNotification({
        type: 'success',
        message: isEditMode
          ? 'Réservation mise à jour avec succès'
          : 'Réservation créée avec succès',
      });

      // Redirect to booking list after a short delay
      setTimeout(() => {
        navigate('/bookings');
      }, 1500);

    } catch (error) {
      console.error('Error saving booking:', error);
      setNotification({
        type: 'error',
        message: `Erreur: ${error.message}`,
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle booking deletion
  const handleDelete = () => {
    setConfirmDialog({
      isOpen: true,
      title: 'Supprimer la réservation',
      message: 'Êtes-vous sûr de vouloir supprimer cette réservation ? Cette action est irréversible.',
      onConfirm: confirmDelete,
    });
  };

  // Confirm booking deletion
  const confirmDelete = async () => {
    try {
      setLoading(true);

      // Construct URL correctly to avoid double slashes
      const deleteUrl = `${API_BASE_URL}/bookings/${propId}/delete/`;
      console.log('Deleting booking at:', deleteUrl);

      const response = await fetch(deleteUrl, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete booking');
      }

      setNotification({
        type: 'success',
        message: 'Réservation supprimée avec succès',
      });

      // Redirect to booking list after a short delay
      setTimeout(() => {
        navigate('/bookings');
      }, 1500);

    } catch (error) {
      console.error('Error deleting booking:', error);
      setNotification({
        type: 'error',
        message: 'Erreur lors de la suppression de la réservation',
      });
    } finally {
      setLoading(false);
      setConfirmDialog({ ...confirmDialog, isOpen: false });
    }
  };

  // Handle status update
  const handleStatusUpdate = async (newStatus) => {
    try {
      setLoading(true);

      // Construct URL correctly to avoid double slashes
      const updateStatusUrl = `${API_BASE_URL}/bookings/${propId}/status/`;
      console.log('Updating booking status at:', updateStatusUrl);

      const response = await fetch(updateStatusUrl, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
        },
        body: JSON.stringify({ status: newStatus }),
      });

      if (!response.ok) {
        throw new Error('Failed to update booking status');
      }

      const data = await response.json();

      setFormData({
        ...formData,
        status: newStatus,
      });

      setNotification({
        type: 'success',
        message: 'Statut de la réservation mis à jour avec succès',
      });

    } catch (error) {
      console.error('Error updating booking status:', error);
      setNotification({
        type: 'error',
        message: 'Erreur lors de la mise à jour du statut de la réservation',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {notification && (
        <Notification
          type={notification.type}
          message={notification.message}
          onClose={() => setNotification(null)}
        />
      )}

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
      />

      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/bookings')}
            className="btn btn-outline flex items-center space-x-2"
          >
            <ArrowBack className="w-5 h-5" />
            <span>Retour</span>
          </button>

          <h1 className="text-2xl font-bold text-gray-900">
            {isEditMode ? 'Modifier la réservation' : 'Nouvelle réservation'}
          </h1>
        </div>

        {isEditMode && (
          <div className="flex items-center space-x-2">
            <button
              onClick={handleDelete}
              className="btn btn-danger flex items-center space-x-2"
              disabled={loading}
            >
              <Delete className="w-5 h-5" />
              <span>Supprimer</span>
            </button>
          </div>
        )}
      </div>

      {isEditMode && formData.booking_reference && (
        <div className="flex items-center justify-between bg-gray-50 p-4 rounded-lg">
          <div>
            <span className="text-gray-500">Référence:</span>
            <span className="ml-2 font-medium">{formData.booking_reference}</span>
          </div>

          <div className="flex items-center space-x-4">
            <span className="text-gray-500">Statut:</span>
            <StatusBadge status={formData.status} />

            <div className="flex items-center space-x-2">
              <select
                value={formData.status}
                onChange={(e) => handleStatusUpdate(e.target.value)}
                className="input py-1 px-2 text-sm"
                disabled={loading}
              >
                <option value="pending">En attente</option>
                <option value="confirmed">Confirmée</option>
                <option value="cancelled">Annulée</option>
                <option value="completed">Terminée</option>
              </select>

              <button
                onClick={() => handleStatusUpdate(formData.status)}
                className="btn btn-sm btn-primary"
                disabled={loading}
              >
                Mettre à jour
              </button>
            </div>
          </div>
        </div>
      )}

      <Card>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-6">
              <h2 className="text-lg font-medium text-gray-900">Informations de réservation</h2>

              <div>
                <label htmlFor="booking_type" className="label">
                  Type de réservation *
                </label>
                <select
                  id="booking_type"
                  name="booking_type"
                  value={formData.booking_type}
                  onChange={handleChange}
                  className="input"
                  required
                >
                  {/* <option value="destination">Destination</option>
                  <option value="excursion">Excursion</option> */}
                  <option value="package">Forfait</option>
                </select>
              </div>

              {formData.booking_type === 'destination' && (
                <>
                  <div>
                    <label htmlFor="destination" className="label">
                      Destination *
                    </label>
                    <select
                      id="destination"
                      name="destination"
                      value={formData.destination}
                      onChange={handleChange}
                      className="input"
                      required
                    >
                      <option value="">Sélectionner une destination</option>
                      {destinationsData?.destinations?.map((destination) => (
                        <option key={destination.id} value={destination.id}>
                          {destination.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="start_date" className="label">
                        Date de début *
                      </label>
                      <input
                        type="date"
                        id="start_date"
                        name="start_date"
                        value={formData.start_date}
                        onChange={handleChange}
                        className="input"
                        required
                      />
                    </div>

                    <div>
                      <label htmlFor="end_date" className="label">
                        Date de fin *
                      </label>
                      <input
                        type="date"
                        id="end_date"
                        name="end_date"
                        value={formData.end_date}
                        onChange={handleChange}
                        className="input"
                        required
                      />
                    </div>
                  </div>
                </>
              )}

              {formData.booking_type === 'excursion' && (
                <>
                  <div>
                    <label htmlFor="excursion" className="label">
                      Excursion *
                    </label>
                    <select
                      id="excursion"
                      name="excursion"
                      value={formData.excursion}
                      onChange={handleChange}
                      className="input"
                      required
                    >
                      <option value="">Sélectionner une excursion</option>
                      {excursionsData?.excursions?.map((excursion) => (
                        <option key={excursion.id} value={excursion.id}>
                          {excursion.title}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="start_date" className="label">
                      Date *
                    </label>
                    <input
                      type="date"
                      id="start_date"
                      name="start_date"
                      value={formData.start_date}
                      onChange={handleChange}
                      className="input"
                      required
                    />
                  </div>
                </>
              )}

              {formData.booking_type === 'package' && (
                <>
                  <div>
                    <label htmlFor="package" className="label">
                      Forfait *
                    </label>
                    <select
                      id="package"
                      name="package"
                      value={formData.package}
                      onChange={handleChange}
                      className="input"
                      required
                    >
                      <option value="">Sélectionner un forfait</option>
                      {packagesData?.packages?.map((pkg) => (
                        <option key={pkg.id} value={pkg.id}>
                          {pkg.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="start_date" className="label">
                      Date de début *
                    </label>
                    <input
                      type="date"
                      id="start_date"
                      name="start_date"
                      value={formData.start_date}
                      onChange={handleChange}
                      className="input"
                      required
                    />
                  </div>
                </>
              )}

              <div>
                <label htmlFor="travelers" className="label">
                  Nombre de voyageurs *
                </label>
                <input
                  type="number"
                  id="travelers"
                  name="travelers"
                  value={formData.travelers}
                  onChange={handleChange}
                  min="1"
                  className="input"
                  required
                />
              </div>

              <div>
                <label htmlFor="total_price" className="label">
                  Prix total (€) *
                </label>
                <input
                  type="number"
                  id="total_price"
                  name="total_price"
                  value={formData.total_price}
                  onChange={handleChange}
                  step="0.01"
                  min="0"
                  className="input"
                  required
                />
              </div>

              {/* Custom Pricing Section */}
              <div className="border-t border-gray-200 pt-6">
                <h3 className="text-md font-medium text-gray-900 mb-4">Options de tarification</h3>

                <div className="space-y-4">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="custom_price_enabled"
                      name="custom_price_enabled"
                      checked={formData.custom_price_enabled}
                      onChange={(e) => setFormData({
                        ...formData,
                        custom_price_enabled: e.target.checked,
                        custom_unit_price: e.target.checked ? formData.custom_unit_price : ''
                      })}
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <label htmlFor="custom_price_enabled" className="ml-2 text-sm font-medium text-gray-700">
                      Utiliser un prix personnalisé
                    </label>
                  </div>

                  {formData.custom_price_enabled && (
                    <div>
                      <label htmlFor="custom_unit_price" className="label">
                        Prix personnalisé par personne (€) *
                      </label>
                      <input
                        type="number"
                        id="custom_unit_price"
                        name="custom_unit_price"
                        value={formData.custom_unit_price}
                        onChange={handleChange}
                        step="0.01"
                        min="0"
                        className="input"
                        required={formData.custom_price_enabled}
                        placeholder="Entrez le prix personnalisé par personne"
                      />
                      {formData.custom_unit_price && formData.travelers && (
                        <p className="mt-2 text-sm text-blue-600">
                          <strong>Calcul automatique:</strong> {formData.custom_unit_price} € × {formData.travelers} voyageur{formData.travelers > 1 ? 's' : ''} = {(parseFloat(formData.custom_unit_price) * parseInt(formData.travelers)).toFixed(2)} €
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <h2 className="text-lg font-medium text-gray-900">Informations client</h2>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="first_name" className="label">
                    Prénom *
                  </label>
                  <input
                    type="text"
                    id="first_name"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleChange}
                    className="input"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="last_name" className="label">
                    Nom *
                  </label>
                  <input
                    type="text"
                    id="last_name"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleChange}
                    className="input"
                    required
                  />
                </div>
              </div>

              <div>
                <label htmlFor="email" className="label">
                  Email *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="input"
                  required
                />
              </div>

              <div>
                <label htmlFor="phone" className="label">
                  Téléphone *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="input"
                  required
                />
              </div>

              <div>
                <label htmlFor="special_requests" className="label">
                  Demandes spéciales
                </label>
                <textarea
                  id="special_requests"
                  name="special_requests"
                  value={formData.special_requests}
                  onChange={handleChange}
                  rows="4"
                  className="input"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate('/bookings')}
              className="btn btn-outline"
              disabled={loading}
            >
              Annuler
            </button>

            <button
              type="submit"
              className="btn btn-primary flex items-center space-x-2"
              disabled={loading}
            >
              <Save className="w-5 h-5" />
              <span>{isEditMode ? 'Mettre à jour' : 'Enregistrer'}</span>
            </button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default BookingFormPage;
